import classNames from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useCurrentUserRegistersQuery } from '@/modules/investor-dashboard/queries/registers.tsx';
import {
  useAddExistingInvestorMutation,
  useRegisterHoldersQuery,
  useRegisterShareholdersQuery,
} from '@/modules/issuer-dashboard/queries/shareholders.tsx';
import {
  Button,
  CustomCheckbox,
  ModalContentWrapper,
  SelectWithCreatable,
  SelectWithCreatableOption,
  Spinner,
} from 'ui';
import { UserManagementIcon } from 'ui/icons';
import { UserRegisterResponse } from 'api';

export interface IssuerAddExistingInvestorModalProps {
  open: boolean;
  onClose: () => void;
}

export const IssuerAddExistingInvestorModal = ({
  open,
  onClose,
}: IssuerAddExistingInvestorModalProps) => {
  const [params] = useSearchParams();
  const currentRegisterId = params.get(REGISTER_ID_SEARCH_PARAM_KEY);

  const { data: registerList, isLoading: isLoadingRegisters } = useCurrentUserRegistersQuery();

  const registerOptions = useMemo<SelectWithCreatableOption<UserRegisterResponse>[]>(
    () =>
      registerList?.data
        ?.filter(item => String(item.register_id) !== String(currentRegisterId))
        ?.map(item => ({
          value: item,
          label: item.register_obj?.name || '',
        })) ?? [],
    [registerList, currentRegisterId],
  );

  const [selectedRegister, setSelectedRegister] = useState<
    SelectWithCreatableOption<UserRegisterResponse> | undefined
  >();

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [hasPermission, setHasPermission] = useState(false);

  const registerId = selectedRegister?.value?.register_id;

  const { data: shareholdersData, isLoading: isLoadingUsers } = useRegisterHoldersQuery(
    registerId,
    !registerId,
  );

  const { data: currentRegisterShareholders } = useRegisterShareholdersQuery(
    {
      register_id: Number(currentRegisterId),
    },
    !currentRegisterId,
  );

  const currentRegisterUserIds = useMemo(() => {
    if (!currentRegisterShareholders?.shareholders) return new Set<number>();

    return new Set(
      currentRegisterShareholders.shareholders
        .map(shareholder => shareholder.user?.id)
        .filter((id): id is number => id !== undefined),
    );
  }, [currentRegisterShareholders]);

  const users = useMemo(() => {
    if (!shareholdersData?.data) return [];

    return shareholdersData.data
      .flatMap(shareholder => (shareholder.user ? [shareholder.user] : []))
      .filter(user => !currentRegisterUserIds.has(user.id));
  }, [shareholdersData, currentRegisterUserIds]);

  const selectedUser = useMemo(
    () => users.find(u => u.id === selectedUserId) ?? null,
    [users, selectedUserId],
  );

  const selectedUserName = useMemo(() => {
    if (!selectedUser) return '';
    return (
      [selectedUser.first_name, selectedUser.last_name].filter(Boolean).join(' ').trim() ||
      selectedUser.email ||
      `User #${selectedUser.id}`
    );
  }, [selectedUser]);

  const handleChangeRegister = useCallback(
    (option: SelectWithCreatableOption<UserRegisterResponse> | null) => {
      if (!option?.value || option.value.register_id === selectedRegister?.value?.register_id) {
        return;
      }

      setSelectedRegister(option);
      setSelectedUserId(null);
      setHasPermission(false);
    },
    [selectedRegister?.value?.register_id],
  );

  const handleUserSelect = useCallback((userId: number) => {
    setSelectedUserId(userId);
    setHasPermission(false);
  }, []);

  const { mutateAsync: addInvestor, isLoading: isAddingInvestor } = useAddExistingInvestorMutation({
    onSuccess: onClose,
  });

  const handleAddInvestor = useCallback(() => {
    if (!selectedUserId || !selectedRegister || !currentRegisterId) return;

    addInvestor({
      source_register_id: selectedRegister.value.register_id,
      target_register_id: Number(currentRegisterId),
      investor_user_id: selectedUserId,
    });
  }, [selectedUserId, selectedRegister, currentRegisterId, addInvestor]);

  return (
    <ModalContentWrapper open={open} title="Add Existing Holder" onClose={onClose}>
      <div className="flex flex-col h-[500px]">
        <div className="flex flex-col gap-6 pb-6">
          {isLoadingRegisters ? (
            <div className="flex items-center justify-center h-20">
              <Spinner size="md" variant="primary" />
            </div>
          ) : (
            <SelectWithCreatable<UserRegisterResponse, string>
              label="From register"
              options={registerOptions}
              value={selectedRegister}
              onChange={handleChangeRegister}
              placeholder="Select a fund/register"
            />
          )}
        </div>

        <div className="flex-1 overflow-y-auto mb-6">
          {selectedRegister ? (
            isLoadingUsers ? (
              <div className="flex items-center justify-center h-full">
                <Spinner size="md" variant="primary" />
              </div>
            ) : users.length ? (
              <div className="space-y-2">
                {users.map(user => {
                  const label =
                    [user.first_name, user.last_name].filter(Boolean).join(' ').trim() ||
                    user.email ||
                    `User #${user.id}`;

                  const isSelected = selectedUserId === user.id;

                  return (
                    <div
                      key={user.id}
                      onClick={() => handleUserSelect(user.id)}
                      className={classNames(
                        'flex gap-3 items-center border rounded-lg py-2.5 px-3 cursor-pointer',
                        {
                          'bg-blue-50 border-blue-300': isSelected,
                          'hover:bg-gray-100': !isSelected,
                        },
                      )}
                    >
                      <div className="flex items-center justify-center w-10 h-10 bg-gray-200 rounded-full">
                        <img src={UserManagementIcon} alt="user" className="w-5 h-5" />
                      </div>
                      <span className="text-sm">{label}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                No users found in this register
              </div>
            )
          ) : (
            !isLoadingRegisters && (
              <div className="flex items-center justify-center h-full text-gray-500">
                Please select a register to view investors
              </div>
            )
          )}
        </div>

        <div className="flex flex-col gap-4 border-t pt-6">
          {selectedUserId && (
            <CustomCheckbox
              checked={hasPermission}
              onChange={e => setHasPermission(e.target.checked)}
              label={
                <span className="text-sm">
                  I have received permission from investor{' '}
                  <span className="font-semibold">{selectedUserName}</span> to be invited to this
                  fund
                </span>
              }
            />
          )}

          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleAddInvestor}
              disabled={!selectedUserId || !hasPermission || isAddingInvestor}
            >
              {isAddingInvestor ? 'Adding…' : 'Add Investor'}
            </Button>
          </div>
        </div>
      </div>
    </ModalContentWrapper>
  );
};
